/**
 * Copyright 2020 (c) Dominik Basner, Sotec GmbH (for VDW e.V.)
 * Copyright 2020 (c) Fabian Wilhelm, Sotec GmbH (for VDW e.V.)
 * Copyright 2020-2022 (c) Christian von Arnim, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2021 (c) Moritz Walker, ISW University of Stuttgart (for umati and VDW e.V.)
 * Copyright 2026 (c) Alexander Uhl, ISW University of Stuttgart (for umati and VDW e.V.)
 */

import { labels } from '@umati/dashboard-common';
import { anyTodo, IFair, IMachine, ISoftware } from '@umati/dashboard-types';
import { MachineryMachinesId } from 'consts/WellKnownIds';
import Fuse from 'fuse.js';
import React, { Component } from 'react';
import { Card, Nav, Navbar } from 'react-bootstrap';
import { connect } from 'react-redux';
import { initialCommonFilterState } from 'redux/reducers/commonFilter';
import { IStore } from 'redux/storeType';
import { IFilterMachineData } from 'utilities/MachinesFilter';
import MachineHandling from 'utilities/MachineHandling';
import FairMap from '../components/fairMap/fairMap';
import FilterModal, { IFilterDescription } from '../components/filterModal/filterModal';
import MachineListElement from '../components/machineList/machineListElement';
import SoftwareListElement from '../components/softwareList/softwareListElement';
import { setCommonListFilter, setCommonListFilterType } from '../redux/actions';
import { getCommonListFilter, getFair, getMachinesByFairAndParent, getSoftwareByFairArray } from '../redux/selectors';

type MixType = IMachine | ISoftware;

interface IMixedListPropsEx {
  softwares: ISoftware[];
  machines: IMachine[];
  fair?: IFair;
  filter: IFilterMachineData;
}

interface IMixedListPropsActions {
  setCommonListFilter: setCommonListFilterType;
}

interface IMixedListPropsAll extends IMixedListPropsActions, IMixedListPropsEx {}

interface IMixedListState {
  filter: IFilterMachineData;
  softwares: ISoftware[];
  machines: IMachine[];
  fuseOptions: anyTodo;
  merged: MixType[];
  manufacturers: string[];
  expanded: Set<string>;
}

class MixedList extends Component<IMixedListPropsAll, IMixedListState> {
  constructor(props) {
    super(props);
    const searchKeys = [
      'name',
      'demoPageShortUrl',
      'demoPageUrl',
      'Data.ComponentName.text',
      'Data.DeviceClass',
      'Data.Manufacturer.text',
      'Data.Manufacturer.Value.Text',
      'Data.Model.text',
      'Data.Model.Value.Text',
      'Data.ProductInstanceUri',
      'Data.ProductInstanceUri.Value',
      'MachineId',
      'ShortId',
      'TypeDefinition',
    ];

    this.handleExpand.bind(this);
    this.filterCallback.bind(this);
    this.mapCallback.bind(this);

    const fuseOptions = { keys: searchKeys, includeScore: true, threshold: 0.4 };
    const machines = this.props.machines;
    const softwares = this.props.softwares;
    const expanded = new Set<string>();
    const merged = MixedList.mergedArray(machines, softwares);
    const [manufacturers] = MixedList.fuzzyReduceManufacturers(MixedList.getManufacturerNames(merged));

    this.state = {
      filter: this.props.filter,
      softwares,
      machines,
      fuseOptions,
      merged,
      manufacturers,
      expanded,
    };
  }

  componentDidMount() {
    const { hash } = window.location;
    if (hash !== '' && hash.replace) {
      let retries = 0;
      const id = hash.replace('#', '');
      const scroll = () => {
        const element = document.getElementById(id);
        if (retries >= 20) {
          return;
        } else if (element) {
          setTimeout(() => element.scrollIntoView(), 0);
        } else {
          setTimeout(scroll, 200);
        }
        retries = retries + 1;
      };
      scroll();
    }
  }

  static getManufacturerNames = (items: MixType[]): string[] =>
    items.map((item) => MachineHandling.getManufacturerName(item)).filter((name): name is string => name !== undefined);

  static mergedArray = (machines, software) => {
    const merged = machines.concat(software);
    merged.sort((a, b) => {
      const manufacturerA = MachineHandling.getManufacturerName(a);
      const manufacturerB = MachineHandling.getManufacturerName(b);
      if (manufacturerA && manufacturerB) {
        return MixedList.compareIgnoreCase(manufacturerA, manufacturerB);
      }
      return 0;
    });
    return merged;
  };

  static getDerivedStateFromProps(nextProps, prevState) {
    const merged = MixedList.mergedArray(nextProps.machines, nextProps.softwares);
    const [manufacturers, manufacturersMapping] = MixedList.fuzzyReduceManufacturers(
      MixedList.getManufacturerNames(merged),
    );
    const newState = {
      ...prevState,
      merged,
      softwares: nextProps.softwares,
      machines: nextProps.machines,
      manufacturers,
      manufacturersMapping,
    };
    const dontUpdateExpanded =
      prevState.machines !== undefined &&
      nextProps.machines !== undefined &&
      prevState.machines.length === nextProps.machines.length &&
      prevState.machines.reduce((acc, val) => acc && nextProps.machines.includes(val), true);
    return MixedList.filter(newState.filter, newState, nextProps.fair, dontUpdateExpanded);
  }

  static compareIgnoreCase = (a, b) => {
    //convert inputs to strings if they exist, or default to an empty string
    const strA = a != null ? String(a).toLowerCase() : '';
    const strB = b != null ? String(b).toLowerCase() : '';

    return strA.localeCompare(strB, 'en');
  };

  static fuzzyReduceManufacturers = (manufacturerArray) => {
    manufacturerArray.sort(MixedList.compareIgnoreCase);
    const manufacturers = new Set<string>(manufacturerArray);
    const mappings: any = {};
    for (const key of manufacturers) {
      mappings[key] = null;
    }
    for (const man of manufacturers) {
      const fuse = new Fuse([...manufacturers], { threshold: 0.15 });
      //ensure 'man' is a valid, trimmed string. Default to empty string if null/undefined.
      const currentMan = typeof man === 'string' ? man.trim() : man ? String(man).trim() : '';
      let searchstring = currentMan;

      if (searchstring) {
        const firstWord = searchstring.split(' ')[0];
        //check the length of the first word
        if (!firstWord || firstWord.length < 3) {
          searchstring = currentMan.substring(0, 4);
        } else {
          searchstring = firstWord;
        }
        //ensure fuse is defined and has a search method before calling it
        const result = typeof fuse?.search === 'function' ? fuse.search(searchstring) : [];
        if (Array.isArray(result)) {
          result.forEach((res) => {
            //ensure res and res.item exist before mapping/deleting
            if (res && res.item !== undefined) {
              mappings[res.item] = currentMan;
              manufacturers.delete(res.item);
            }
          });
        }
      }
    }
    const fuzzyManufacturers = new Set<string>();
    for (const map in mappings) {
      fuzzyManufacturers.add(mappings[map]);
    }
    return [[...fuzzyManufacturers], mappings];
  };

  isExpanded(el: IMachine | ISoftware): boolean {
    const elMachine = el as IMachine;
    const elSoftware = el as ISoftware;
    if (elMachine.MachineId) {
      return this.state.expanded.has(elMachine.MachineId);
    }
    if (elSoftware.SoftwareId) {
      return this.state.expanded.has(elSoftware.SoftwareId);
    }
    return false;
  }

  static expand = (key, expSet) => {
    if (expSet.has(key)) {
      expSet.delete(key);
    } else {
      expSet.add(key);
    }
    return expSet;
  };

  handleExpand = (key) => {
    const expSet = MixedList.expand(key, this.state.expanded);
    this.setState({ ...this.state, expanded: expSet });
  };

  mapCallback = (data) => {
    this.setState({ ...this.state, filter: { ...this.state.filter, halls: data } });
  };

  static filter = (filterData: IFilterMachineData, state: IMixedListState, fair, ignoreDetails = false) => {
    state.filter = filterData;
    const merged = MixedList.mergedArray(state.machines, state.softwares);
    const fuse = new Fuse(merged, state.fuseOptions);

    const result = filterData.searchValue ? fuse.search(filterData.searchValue).map((m) => m.item) : merged;

    if (!ignoreDetails) {
      if (filterData.showDetails) {
        state.expanded = new Set(result.map((m) => m.key));
      } else {
        state.expanded = new Set();
      }
    }

    if (fair?.Halls && filterData.halls && filterData.halls !== 'all') {
      state.merged = result.filter((sw) => sw.Positions[fair.Tag].hall === filterData.halls);
    } else {
      state.merged = result;
    }

    if (filterData.showFavourites) {
      state.merged = state.merged.filter((el) => {
        return el.Favorite;
      });
    }

    state.merged = state.merged.sort((a, b) => {
      const manufacturerA = MachineHandling.getManufacturerName(a);
      const manufacturerB = MachineHandling.getManufacturerName(b);
      if (manufacturerA && manufacturerB) {
        return MixedList.compareIgnoreCase(manufacturerA, manufacturerB);
      } else {
        return 0;
      }
    });

    state.filter.fairTag = fair?.Tag;
    return state;
  };

  filterCallback = (filterData) => {
    const state = MixedList.filter(filterData, this.state, this.props.fair);
    this.props.setCommonListFilter(state.filter);
    this.setState(state);
  };

  static isExpanded = (el, expds) => {
    return expds.has(el.key);
  };

  render() {
    const defaults = initialCommonFilterState;
    const fairTag = this.props.fair?.Tag;
    const filter: IFilterDescription = {
      checkboxes: [
        {
          cbKey: 'showDetails',
          label: 'Details',
          value: this.state.filter.showDetails,
          default: defaults.showDetails,
          toggleButton: true,
        },
        {
          cbKey: 'showFavourites',
          label: labels.favorites,
          value: this.state.filter.showFavourites,
          default: defaults.showFavourites,
          toggleButton: false,
        },
        {
          cbKey: 'showManufacturers',
          label: 'Show manufacturers',
          value: !!this.state.filter.showManufacturers,
          default: !!defaults.showManufacturers,
          toggleButton: false,
        },
      ],
      searchbars: [
        {
          cbKey: 'searchValue',
          label: labels.searchWithDots,
          value: this.state.filter.searchValue,
          default: defaults.searchValue,
        },
      ],
    };

    if (fairTag !== 'all') {
      filter.dropdowns = [
        {
          cbKey: 'halls',
          label: labels.hall,
          choices: ['all', ...(this.props.fair?.Halls || [])],
          value: this.state.filter.halls,
          default: defaults.halls,
        },
      ];
      filter.toggles = [
        {
          cbKey: 'show',
          choices: [
            { value: 'list', label: 'List', icon: 'btn-icon-list' },
            { value: 'map', label: 'Map', icon: 'btn-icon-map' },
          ],
          value: this.state.filter.show,
          default: defaults.show,
        },
      ];
    }

    const child = {
      type: 'dropdowns',
      idx: 0,
      cbKey: 'halls',
      isVisible: this.state.filter.show === 'map',
    };

    let lastManufacturer = '';
    return (
      <div className="container view-list">
        <FilterModal filter={filter} callback={this.filterCallback} child={child}>
          <FairMap fair={this.props.fair} locationHall={this.state.filter.halls} />
        </FilterModal>
        {this.state.filter.showManufacturers && (
          <div className="sidenav">
            <Card>
              <Navbar>
                <Nav className="mr-auto">
                  {this.state.manufacturers.map((m) =>
                    m && m.replace ? (
                      <Nav.Link
                        style={{ whiteSpace: 'nowrap' }}
                        href={`${window.location.pathname}#${m.replace(/\s+/g, '')}`}
                        key={m}
                      >
                        {m.split(' ').splice(0, 2).join(' ')}
                      </Nav.Link>
                    ) : (
                      <></>
                    ),
                  )}
                </Nav>
              </Navbar>
            </Card>
          </div>
        )}
        <ul className="list-group">
          {this.state.merged.map((el) => {
            let id: string | null = null;
            const elMachine = el as IMachine;
            const elSoftware = el as ISoftware;
            const manufacturer = MachineHandling.getManufacturerName(el);
            if (manufacturer !== lastManufacturer) {
              id = manufacturer ?? null;
              lastManufacturer = manufacturer ?? '';
            }
            if (elMachine.MachineId) {
              return (
                <div key={elMachine.MachineId}>
                  <a className="anchor" id={id?.replace ? id.replace(/\s+/g, '') : ''} />
                  <MachineListElement
                    key={elMachine.MachineId}
                    machine={elMachine}
                    in={MixedList.isExpanded(el, this.state.expanded)}
                    fairTag={this.props.fair?.Tag || ''}
                    cb={{ expand: () => this.handleExpand(elMachine.MachineId) }}
                  />
                </div>
              );
            } else {
              return (
                <div key={elSoftware.SoftwareId}>
                  <a className="anchor" id={id?.replace ? id?.replace(/\s+/g, '') : ''} />
                  <SoftwareListElement
                    key={elSoftware.SoftwareId}
                    {...elSoftware}
                    in={MixedList.isExpanded(el, this.state.expanded)}
                    fairTag={this.props.fair?.Tag || ''}
                    cb={{ expand: () => this.handleExpand(elSoftware.SoftwareId) }}
                  />
                </div>
              );
            }
          })}
        </ul>
      </div>
    );
  }
}

const mapStateToProps = (state: IStore, ownProps) => {
  const fairTag = ownProps.match.params.fair;
  const machines = getMachinesByFairAndParent(state, fairTag, MachineryMachinesId);
  return {
    softwares: getSoftwareByFairArray(state, fairTag),
    machines,
    fair: getFair(state, ownProps.match.params.fair),
    filter: getCommonListFilter(state, fairTag),
  };
};

export default connect(mapStateToProps, { setCommonListFilter })(MixedList);
